import { SurveyQuestion } from '../types/Survey.type';

type SubQuestionRenderer = (subQuestion: SurveyQuestion) => string;

export type GroupSubQuestionRenderers = {
  radio: SubQuestionRenderer;
  checkbox: SubQuestionRenderer;
  slider: SubQuestionRenderer;
  /** Also renders `textarea` sub-questions. */
  input: SubQuestionRenderer;
  date: SubQuestionRenderer;
};

/**
 * Shared scaffold for rendering a group question's sub-answers: resolves the
 * group's answer map, injects each sub-answer onto its sub-question (so the
 * per-type renderers read it from `surveyQuestion.answer` exactly as for a
 * top-level question), and concatenates the rendered strings in order.
 *
 * The output format lives entirely in the caller's renderers — buildSurveyStr
 * and completedSurveysForNotes intentionally render the same answers
 * differently.
 */
export const buildGroupSubQuestionsStr = (
  surveyQuestion: SurveyQuestion,
  renderers: GroupSubQuestionRenderers
): string => {
  let answerStr = '';

  const groupValue =
    surveyQuestion?.answer?.type === 'group' ? surveyQuestion.answer.value : undefined;

  surveyQuestion?.typeOptions?.subQuestions?.forEach((subQuestion: SurveyQuestion) => {
    const subAnswer = groupValue?.[subQuestion.id];

    if (!subAnswer) return;

    const subWithAnswer: SurveyQuestion = { ...subQuestion, answer: subAnswer };

    switch (subQuestion.type) {
      case 'radio':
        answerStr += renderers.radio(subWithAnswer);
        break;
      case 'checkbox':
        answerStr += renderers.checkbox(subWithAnswer);
        break;
      case 'slider':
        answerStr += renderers.slider(subWithAnswer);
        break;
      case 'input':
      case 'textarea':
        answerStr += renderers.input(subWithAnswer);
        break;
      case 'date':
        answerStr += renderers.date(subWithAnswer);
        break;
      default:
        break;
    }
  });

  return answerStr;
};
